/**
 * DhulHijjahDashboard — first 10 days of Dhul Hijjah are among the most
 * blessed days of the year (Bukhari 969). This widget surfaces only on
 * 1-10 Dhul Hijjah and helps the user maintain dhikr + good deeds.
 *
 * Sections:
 *   1. Big "Day N of 10" header with progress arc
 *   2. Dhikr counter (Tasbeeh / Tahleel / Takbeer / Tahmeed) — tap to add
 *      to today's count, persists per Hijri day
 *   3. "Today's recommended deed" — rotates daily (fasting, charity,
 *      Quran, dhikr, Salah on time, du'a, kindness, istighfar)
 *   4. Day of Arafah special card (active only on day 9):
 *        "Fasting today expiates 2 years of sins" — Muslim 1162
 *   5. Eid al-Adha button (active only on day 10)
 */

import type { Widget, WidgetContext } from '../types';
import { gregorianToHijri } from '../../../services/HijriDate';

interface DhikrEntry { id: string; arabic: string; translit: string; meaning: Record<string, string>; }

const DHIKR: DhikrEntry[] = [
  {
    id: 'tasbeeh', arabic: 'سُبْحَانَ اللَّهِ', translit: 'Subhanallah',
    meaning: { en: 'Glory be to Allah', fr: 'Gloire à Allah', ar: 'سبحان الله', es: 'Gloria a Allah', de: 'Ehre sei Allah' },
  },
  {
    id: 'tahleel', arabic: 'لَا إِلَٰهَ إِلَّا اللَّهُ', translit: 'La ilaha illallah',
    meaning: { en: 'There is no god but Allah', fr: 'Il n\'y a de divinité qu\'Allah', ar: 'لا إله إلا الله', es: 'No hay más dios que Allah', de: 'Es gibt keine Gottheit außer Allah' },
  },
  {
    id: 'takbeer', arabic: 'اللَّهُ أَكْبَرُ', translit: 'Allahu akbar',
    meaning: { en: 'Allah is the Greatest', fr: 'Allah est le plus Grand', ar: 'الله أكبر', es: 'Allah es el más Grande', de: 'Allah ist der Größte' },
  },
  {
    id: 'tahmeed', arabic: 'الْحَمْدُ لِلَّهِ', translit: 'Alhamdulillah',
    meaning: { en: 'Praise be to Allah', fr: 'Louange à Allah', ar: 'الحمد لله', es: 'Alabado sea Allah', de: 'Lob sei Allah' },
  },
];

const DEEDS_KEYS = [
  'deed_fast', 'deed_quran', 'deed_charity', 'deed_dhikr',
  'deed_salah_on_time', 'deed_kindness', 'deed_istighfar', 'deed_dua',
  'deed_visit_relative', 'deed_smile',
];

export const DhulHijjahDashboard: Widget = {
  id: 'dhul-hijjah-dashboard',
  label: {
    en: 'Dhul Hijjah',
    fr: 'Dhou al-Hijjah',
    ar: 'ذو الحجة',
    es: 'Dhul Hiyya',
    de: 'Dhul-Hijjah',
  },
  icon: '🕋',
  defaultVisible: true,
  defaultOrder: 26,
  defaultSize: 'compact',
  visibilityRule: () => {
    try {
      const h = gregorianToHijri(new Date());
      return h.month === 12 && h.day >= 1 && h.day <= 10;
    } catch { return false; }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    let h;
    try { h = gregorianToHijri(new Date()); } catch { container.innerHTML = ''; return; }
    if (h.month !== 12 || h.day > 10) { container.innerHTML = ''; return; }
    const day = h.day;
    const lang = (ctx.locale || 'en').split('-')[0];

    // Per-day dhikr counters
    const counterKey = `aweni_dh_dhikr_${h.year}_${day}`;
    let counts: Record<string, number> = {};
    try { counts = JSON.parse(localStorage.getItem(counterKey) || '{}') as Record<string, number>; } catch { /* */ }

    const total = DHIKR.reduce((s, d) => s + (counts[d.id] || 0), 0);
    const todayDeed = t(DEEDS_KEYS[(day - 1) % DEEDS_KEYS.length]) || DEEDS_KEYS[(day - 1) % DEEDS_KEYS.length];
    const isArafah = day === 9;
    const isEid = day === 10;

    container.innerHTML = `
      <div style="background:linear-gradient(135deg, #052e16 0%, #14532d 100%);color:#fff;border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🕋</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_dh_title') || 'First 10 of Dhul Hijjah')}</span>
          <span style="margin-inline-start:auto;font-size:11px;opacity:0.85;">${escapeHtml(t('widget_dh_day') || 'Day')} ${day}/10</span>
        </div>

        ${isArafah ? `
          <div style="background:#fbbf24;color:#0a0a0a;padding:10px;border-radius:10px;margin-bottom:10px;">
            <div style="font-size:13px;font-weight:800;">${escapeHtml(t('widget_dh_arafah_title') || 'Day of Arafah')}</div>
            <div style="font-size:11px;margin-top:2px;">${escapeHtml(t('widget_dh_arafah_text') || 'Fasting today expiates 2 years of sins. — Muslim 1162')}</div>
          </div>
        ` : ''}

        ${isEid ? `
          <div style="background:#fbbf24;color:#0a0a0a;padding:10px;border-radius:10px;margin-bottom:10px;">
            <div style="font-size:13px;font-weight:800;">🎉 ${escapeHtml(t('widget_dh_eid_title') || 'Eid al-Adha mubarak')}</div>
            <div style="font-size:11px;margin-top:2px;">${escapeHtml(t('widget_dh_eid_text') || 'Eid prayer + udhiya day.')}</div>
          </div>
        ` : ''}

        <!-- Today's recommended deed -->
        <div style="background:rgba(255,255,255,0.08);border-radius:10px;padding:10px;margin-bottom:10px;">
          <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;opacity:0.85;margin-bottom:4px;">${escapeHtml(t('widget_dh_deed') || 'Recommended deed today')}</div>
          <div style="font-size:14px;font-weight:600;">✨ ${escapeHtml(todayDeed)}</div>
        </div>

        <!-- Dhikr counter -->
        <div style="background:rgba(255,255,255,0.08);border-radius:10px;padding:10px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;opacity:0.85;flex:1;">${escapeHtml(t('widget_dh_dhikr_today') || 'Dhikr today')}</span>
            <span style="font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;">${total}</span>
          </div>
          <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:6px;">
            ${DHIKR.map((d) => `
              <button type="button" data-dhikr="${d.id}"
                style="display:flex;flex-direction:column;align-items:flex-start;gap:2px;padding:8px 10px;border:1px solid rgba(255,255,255,0.18);border-radius:10px;background:rgba(255,255,255,0.04);color:#fff;cursor:pointer;font:inherit;text-align:start;min-height:54px;">
                <span style="font-size:14px;font-weight:700;font-family:'Amiri','Scheherazade New',serif;">${escapeHtml(d.arabic)}</span>
                <span style="display:flex;align-items:center;gap:6px;font-size:11px;width:100%;">
                  <span style="opacity:0.8;flex:1;">${escapeHtml(d.translit)}</span>
                  <span style="font-weight:800;font-variant-numeric:tabular-nums;">${counts[d.id] || 0}</span>
                </span>
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    container.querySelectorAll<HTMLButtonElement>('[data-dhikr]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.dhikr || '';
        counts = { ...counts, [id]: (counts[id] || 0) + 1 };
        try { localStorage.setItem(counterKey, JSON.stringify(counts)); } catch { /* */ }
        DhulHijjahDashboard.render(container, size, ctx);
      });
    });
    void lang;
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
